import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, Loader2, ShieldCheck } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Logo } from "@/components/brand/Logo";
import { Seo } from "@/components/brand/Seo";

const inputClass =
  "w-full rounded-xl border border-[#E4DED2] bg-white px-3.5 py-2.5 text-[15px] outline-none transition-colors placeholder:text-[#B5AEA1] focus:border-[#F2811D]";

function Field({ label, hint, required, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.08em] text-[#6B6459]">
        {label}
        {required && <span className="text-[#F2811D]"> *</span>}
      </span>
      {children}
      {hint && <span className="mt-1.5 block text-xs leading-relaxed body-text">{hint}</span>}
    </label>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#F7F4EF]">
      <div className="mx-auto flex max-w-[560px] flex-col px-5 py-10 sm:py-16">
        <Logo height={30} />
        {children}
      </div>
    </div>
  );
}

export default function InviteJoin() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [state, setState] = useState("checking");
  const [linkError, setLinkError] = useState("");
  const [mode, setMode] = useState("register");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [joined, setJoined] = useState(null);

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    password: "",
    confirm: "",
    whatsapp_consent: true,
  });
  const [signIn, setSignIn] = useState({ email: "", password: "" });

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/public/invites/${token}`)
      .then(() => !cancelled && setState("ok"))
      .catch((e) => {
        if (cancelled) return;
        setLinkError(apiError(e));
        setState("invalid");
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const set = (key) => (event) =>
    setForm((f) => ({
      ...f,
      [key]: event.target.type === "checkbox" ? event.target.checked : event.target.value,
    }));

  const register = async (event) => {
    event.preventDefault();
    if (busy) return;
    setError("");
    if (form.password !== form.confirm) {
      setError("The two passwords do not match.");
      return;
    }
    if (form.password.length < 8) {
      setError("Use a password of at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/public/invites/${token}/register`, {
        name: form.name,
        phone: form.phone,
        email: form.email,
        password: form.password,
        whatsapp_consent: form.whatsapp_consent,
      });
      setJoined({ campaign: data.campaign_name, existing: false });
      await refresh();
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const joinExisting = async (event) => {
    event.preventDefault();
    if (busy) return;
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post(`/public/invites/${token}/sign-in-join`, signIn);
      setJoined({ campaign: data.campaign_name, existing: data.already_assigned });
      await refresh();
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (state === "checking") {
    return (
      <Shell>
        <div className="mt-12 flex items-center gap-3 body-text" data-testid="invite-checking">
          <Loader2 className="h-4 w-4 animate-spin" /> Checking this invitation…
        </div>
      </Shell>
    );
  }

  if (state === "invalid") {
    return (
      <Shell>
        <Seo title="Invitation — AnyTribe" description="AnyTribe creator invitation." />
        <div
          className="mt-10 rounded-2xl border border-[#E4DED2] bg-white p-7"
          data-testid="invite-invalid"
        >
          <h1 className="font-heading text-2xl font-extrabold">This link can't be used</h1>
          <p className="mt-3 text-[15px] leading-relaxed body-text" data-testid="invite-link-error">
            {linkError}
          </p>
          <p className="mt-4 text-[15px] leading-relaxed body-text">
            Ask your AnyTribe contact for a new invitation link, or{" "}
            <a href="/login" className="font-bold text-[#F2811D] hover:underline">
              sign in
            </a>{" "}
            if you already have an account.
          </p>
        </div>
      </Shell>
    );
  }

  if (joined) {
    return (
      <Shell>
        <div
          className="mt-10 rounded-2xl border border-[#E4DED2] bg-white p-7"
          data-testid="invite-success"
        >
          <CheckCircle2 className="h-9 w-9 text-[#F2811D]" />
          <h1 className="mt-4 font-heading text-2xl font-extrabold">
            {joined.existing ? "You're already on this campaign" : "You're in"}
          </h1>
          <p className="mt-3 text-[15px] leading-relaxed body-text">
            {joined.existing
              ? `You were already assigned to ${joined.campaign}. Open your portal to add posting links and insights.`
              : `You've joined ${joined.campaign}. Your portal is where you add posting links, submit insights and send invoices.`}
          </p>
          <button
            type="button"
            onClick={() => navigate("/creator")}
            className="mt-6 rounded-full bg-[#F2811D] px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-[#D96E12]"
            data-testid="invite-go-to-portal"
          >
            Go to my campaigns
          </button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <Seo
        title="You've been invited by AnyTribe"
        description="Register as an AnyTribe creator."
      />
      <div className="mt-8 rounded-2xl border border-[#E4DED2] bg-white p-6 sm:p-8">
        <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#F2811D]">
          Creator invitation
        </p>
        <h1 className="mt-3 font-heading text-[26px] font-extrabold leading-tight sm:text-3xl">
          You've been invited by AnyTribe
        </h1>
        <p className="mt-3 text-[15px] leading-relaxed body-text">
          Register below and you'll be added to the campaign automatically. Your AnyTribe contact
          will share the brief and deliverables with you.
        </p>
        <p className="mt-4 flex items-start gap-2 text-xs leading-relaxed body-text">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#6B6459]" />
          This link only ever creates a creator account. It cannot give access to client or
          AnyTribe team areas.
        </p>

        <div className="mt-6 flex gap-2 border-b border-[#EDE7DC]" data-testid="invite-mode-tabs">
          {[
            { key: "register", label: "I'm new here" },
            { key: "signin", label: "I already have an account" },
          ].map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => {
                setMode(tab.key);
                setError("");
              }}
              className={`-mb-px border-b-2 px-1 pb-2.5 text-sm font-bold transition-colors ${
                mode === tab.key
                  ? "border-[#F2811D] text-[#111111]"
                  : "border-transparent text-[#8A8276] hover:text-[#111111]"
              }`}
              data-testid={`invite-tab-${tab.key}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {error && (
          <p
            className="mt-5 rounded-xl border border-[#E7C4C4] bg-[#FCF3F3] px-3.5 py-2.5 text-sm text-[#8C2F2F]"
            data-testid="invite-error"
          >
            {error}
          </p>
        )}

        {mode === "register" ? (
          <form onSubmit={register} className="mt-5 space-y-4" data-testid="invite-register-form">
            <Field label="Full name" required>
              <input
                required
                value={form.name}
                onChange={set("name")}
                className={inputClass}
                placeholder="As you'd like it on invoices"
                data-testid="invite-name-input"
              />
            </Field>
            <Field label="Mobile number" required hint="Malaysian numbers only, e.g. 012-345 6789">
              <input
                required
                type="tel"
                inputMode="tel"
                value={form.phone}
                onChange={set("phone")}
                className={inputClass}
                placeholder="012-345 6789"
                data-testid="invite-phone-input"
              />
            </Field>
            <Field label="Email" required hint="You'll use this to sign in.">
              <input
                required
                type="email"
                value={form.email}
                onChange={set("email")}
                className={inputClass}
                placeholder="you@example.com"
                data-testid="invite-email-input"
              />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Password" required hint="At least 8 characters.">
                <input
                  required
                  type="password"
                  value={form.password}
                  onChange={set("password")}
                  className={inputClass}
                  data-testid="invite-password-input"
                />
              </Field>
              <Field label="Confirm password" required>
                <input
                  required
                  type="password"
                  value={form.confirm}
                  onChange={set("confirm")}
                  className={inputClass}
                  data-testid="invite-confirm-input"
                />
              </Field>
            </div>
            <label className="flex items-start gap-2.5 rounded-xl bg-[#F7F4EF] p-3.5">
              <input
                type="checkbox"
                checked={form.whatsapp_consent}
                onChange={set("whatsapp_consent")}
                className="mt-0.5 h-4 w-4 accent-[#F2811D]"
                data-testid="invite-consent-checkbox"
              />
              <span className="text-sm leading-relaxed body-text">
                I'm happy for AnyTribe to message me on WhatsApp about campaigns I'm on and payment
                confirmations. You can withdraw this any time from your account page.
              </span>
            </label>
            <button
              type="submit"
              disabled={busy}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-[#F2811D] px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-[#D96E12] disabled:opacity-60"
              data-testid="invite-register-submit"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              Register and join
            </button>
          </form>
        ) : (
          <form onSubmit={joinExisting} className="mt-5 space-y-4" data-testid="invite-signin-form">
            <p className="text-sm leading-relaxed body-text">
              Sign in with your existing AnyTribe creator account and we'll add you to this
              campaign. No second account is created.
            </p>
            <Field label="Email" required>
              <input
                required
                type="email"
                value={signIn.email}
                onChange={(e) => setSignIn((s) => ({ ...s, email: e.target.value }))}
                className={inputClass}
                data-testid="invite-signin-email"
              />
            </Field>
            <Field label="Password" required>
              <input
                required
                type="password"
                value={signIn.password}
                onChange={(e) => setSignIn((s) => ({ ...s, password: e.target.value }))}
                className={inputClass}
                data-testid="invite-signin-password"
              />
            </Field>
            <button
              type="submit"
              disabled={busy}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-[#F2811D] px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-[#D96E12] disabled:opacity-60"
              data-testid="invite-signin-submit"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              Sign in and join
            </button>
          </form>
        )}
      </div>
    </Shell>
  );
}
